import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Steps, Space, Button, Switch, Typography, Divider, Pagination, Select, InputNumber, Image, message, Radio, Input, Empty, Spin, Alert } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { getDebtDetail } from '../../services/debt';
import { listUserSeals } from '../../services/auth';
import { computeNoticeContentSig, isStoredSealOffsetsValid, loadState, saveState } from './PublishNoticeEditContent/state';
import { extractPlaceholders } from '../../components/NoticeDocEditor/utils';
import { formatAmountText } from '../../utils/numberFormat';
import { labelOfDebtType } from '../../constants/debtEnums';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import PdfPreview from './PublishNoticeSignature/PdfPreview';
import usePreviewNoticeBodyPdf from './PublishNoticeSignature/usePreviewNoticeBodyPdf';
import useSmartSealDefaultPosition from './PublishNoticeSignature/useSmartSealDefaultPosition';

const { Text } = Typography;

const DEFAULT_SEAL_WIDTH_MM_ENTERPRISE = 42;
const DEFAULT_SEAL_WIDTH_MM_PERSONAL = 30;

const clampNumber = (v, min, max) => Math.max(min, Math.min(max, v));
// 国发〔1999〕25号第十四条：企业事业单位、社会团体印章直径不得大于4.5cm
const SEAL_WIDTH_MM_MIN = 10;
const SEAL_WIDTH_MM_MAX = 45;
const SEAL_WIDTH_LIMITS = { min: SEAL_WIDTH_MM_MIN, max: SEAL_WIDTH_MM_MAX };

const clampSealWidthMm = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return SEAL_WIDTH_LIMITS.min;
  return clampNumber(n, SEAL_WIDTH_LIMITS.min, SEAL_WIDTH_LIMITS.max);
};

// 默认页面设置：当无法从编辑步骤恢复 pageSettings 时，提供可用的 A4 mm 尺寸与边距
const DEFAULT_PAGE_SETTINGS = {
  widthMm: 210,
  heightMm: 297,
  marginTopMm: 25,
  marginRightMm: 25,
  marginBottomMm: 25,
  marginLeftMm: 25,
};

/**
 * 催告发布步骤4：签章与预览
 * 选择印签并在预览中叠加展示（仅前端预览叠加，正式文件生成在后续步骤实现）。
 */
const PublishNoticeSignaturePage = () => {
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const { id } = useParams();
  const [state] = useState(() => loadState());
  // 内容签名：用于将“签章偏移”与“当前内容/页面设置”绑定，避免内容变更后复用旧偏移
  // 说明：编辑内容或页面设置发生变化时，签名会变化，旧偏移会被判定为无效并回退到默认值
  const noticeContentSig = computeNoticeContentSig(state.contentHtml, state.pageSettings);
  const initialSealType = state.sealType || 'E';
  const hasInitialSealWidth = Number.isFinite(Number(state.sealWidthMm)) && Number(state.sealWidthMm) > 0;
  const [applySignature, setApplySignature] = useState(!!state.applySignature || false);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [sealLoading, setSealLoading] = useState(false);
  const [sealList, setSealList] = useState([]);
  const [sealType, setSealType] = useState(initialSealType);
  const [sealTypeTouched, setSealTypeTouched] = useState(false);
  const [sealId, setSealId] = useState(state.sealId || null);
  const [sealUrl, setSealUrl] = useState(state.sealUrl || '');
  const [sealAspect, setSealAspect] = useState(Number.isFinite(Number(state.sealAspect)) && Number(state.sealAspect) > 0 ? Number(state.sealAspect) : 1);
  const [sealWidthMm, setSealWidthMm] = useState(() => clampSealWidthMm(
    hasInitialSealWidth
      ? Number(state.sealWidthMm)
      : (initialSealType === 'E' ? DEFAULT_SEAL_WIDTH_MM_ENTERPRISE : DEFAULT_SEAL_WIDTH_MM_PERSONAL),
  ));
  const [sealWidthTouched, setSealWidthTouched] = useState(false);
  const sealWidthLimits = useMemo(() => SEAL_WIDTH_LIMITS, []);
  // 内容区可移动范围（mm）：用于限制“距右/距底”在内容区内，避免印章跑出页面边距
  // 计算方式：内容区宽高 - 印章宽高（印章宽高也以 mm 表示）
  const maxOffsets = useMemo(() => {
    const ps = state.pageSettings || DEFAULT_PAGE_SETTINGS;
    const contentW = Number(ps.widthMm || 210) - Number(ps.marginLeftMm || 0) - Number(ps.marginRightMm || 0);
    const contentH = Number(ps.heightMm || 297) - Number(ps.marginTopMm || 0) - Number(ps.marginBottomMm || 0);
    const stampW = Number(sealWidthMm || 0);
    const stampH = Number(sealWidthMm || 0) * Number(sealAspect || 1);
    return {
      maxRight: Math.max(0, contentW - stampW),
      maxBottom: Math.max(0, contentH - stampH),
    };
  }, [sealAspect, sealWidthMm, state.pageSettings]);
  // 只有 sealOffsetsSig 与当前内容签名一致时，才认为 sessionStorage 里存的偏移仍然有效
  // 目的：避免用户修改模板内容后，复用旧偏移导致“落章位置错误”
  const hasValidStoredSealOffsets = isStoredSealOffsetsValid({
    contentHtml: state.contentHtml,
    pageSettings: state.pageSettings,
    noticeContentSig,
    sealOffsetsSig: state.sealOffsetsSig,
    sealOffsetRightMm: state.sealOffsetRightMm,
    sealOffsetBottomMm: state.sealOffsetBottomMm,
  });
  const [sealOffsetRightMm, setSealOffsetRightMm] = useState(hasValidStoredSealOffsets ? Number(state.sealOffsetRightMm) : 8);
  const [sealOffsetBottomMm, setSealOffsetBottomMm] = useState(hasValidStoredSealOffsets ? Number(state.sealOffsetBottomMm) : 8);
  const hasInitialSealOffsets = hasValidStoredSealOffsets;
  const [sealKeyword, setSealKeyword] = useState('');
  const [lastSealByType, setLastSealByType] = useState(state.lastSealByType || {});
  const [previewIndex, setPreviewIndex] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, startRightMm: 0, startBottomMm: 0, rect: null, pageSettings: null, lastRightMm: null, lastBottomMm: null });
  const offsetsRef = useRef({ rightMm: sealOffsetRightMm, bottomMm: sealOffsetBottomMm, widthMm: sealWidthMm, heightMm: sealWidthMm * sealAspect });
  const autoSealAppliedRef = useRef(false);
  const lastSmartOffsetsRef = useRef(null);

  useEffect(() => {
    offsetsRef.current = { rightMm: sealOffsetRightMm, bottomMm: sealOffsetBottomMm, widthMm: sealWidthMm, heightMm: sealWidthMm * sealAspect };
  }, [sealOffsetRightMm, sealOffsetBottomMm, sealWidthMm, sealAspect]);

  // 当页面设置/印章尺寸变化时，自动将当前偏移夹紧到合法范围，并立即持久化（防止出现“输入框显示正常但预览溢出”的状态）
  useEffect(() => {
    const nextRight = clampNumber(Number(sealOffsetRightMm || 0), 0, Number(maxOffsets.maxRight || 0));
    const nextBottom = clampNumber(Number(sealOffsetBottomMm || 0), 0, Number(maxOffsets.maxBottom || 0));
    const changed = nextRight !== sealOffsetRightMm || nextBottom !== sealOffsetBottomMm;
    if (!changed) return;
    setSealOffsetRightMm(nextRight);
    setSealOffsetBottomMm(nextBottom);
    saveState({ sealOffsetRightMm: nextRight, sealOffsetBottomMm: nextBottom, sealOffsetsSig: noticeContentSig });
  }, [maxOffsets.maxBottom, maxOffsets.maxRight, noticeContentSig, sealOffsetBottomMm, sealOffsetRightMm]);

  useEffect(() => {
    if (!sealUrl) return;
    let alive = true;
    const img = new window.Image();
    img.onload = () => {
      if (!alive) return;
      const w = Number(img.naturalWidth || 0);
      const h = Number(img.naturalHeight || 0);
      if (w > 0 && h > 0) {
        const nextAspect = h / w;
        setSealAspect(nextAspect);
        saveState({ sealAspect: nextAspect });
      }
    };
    img.onerror = () => {};
    img.src = sealUrl;
    return () => { alive = false; };
  }, [sealUrl]);

  useEffect(() => {
    if (!state.selectedDebtId && !id) {
      navigate('/debt-notice/publish');
      return;
    }
    if (!state.saved) {
      const debtId = id || state.selectedDebtId;
      navigate(`/debt-notice/publish/edit-content/${debtId}`);
      return;
    }
    const debtId = id || state.selectedDebtId;
    getDebtDetail(debtId).then(({ data }) => setSelectedDebt(data)).catch(() => {});
  }, [id, navigate, state.saved, state.selectedDebtId]);

  useEffect(() => {
    if (sealTypeTouched) return;
    if (state.sealType) return;
    setSealType('E');
  }, [sealTypeTouched, state.sealType]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setSealLoading(true);
      try {
        const { data } = await listUserSeals(sealType);
        if (!alive) return;
        setSealList(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!alive) return;
        setSealList([]);
      } finally {
        if (alive) setSealLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [sealType]);

  // 校验当前选择的签章是否仍在列表中（避免 sessionStorage 中缓存了已被删除的签章）
  useEffect(() => {
    if (sealLoading) return;
    if (!sealId && !sealUrl) return;
    // 检查当前 sealId 是否在新的签章列表中
    const exists = sealList.some(s => s.id === sealId);
    if (!exists) {
      // 当前选择的签章不在列表中，清除选择
      setSealId(null);
      setSealUrl('');
      saveState({ sealId: null, sealUrl: '' });
    }
  }, [sealLoading, sealId, sealList]);

  const prev = () => {
    const debtId = id || state.selectedDebtId;
    navigate(`/debt-notice/publish/edit-content/${debtId}`);
  };

  const next = () => {
    // 进入下一步前持久化：同时写入 sealOffsetsSig，确保偏移与当前内容绑定
    saveState({ applySignature, sealId, sealUrl, sealAspect, sealWidthMm, sealOffsetRightMm, sealOffsetBottomMm, sealOffsetsSig: noticeContentSig });
    const debtId = id || state.selectedDebtId;
    navigate(`/debt-notice/publish/generate-pdf/${debtId}`);
  };

  const selectedDebtors = useMemo(() => {
    const list = Array.isArray(state.selectedDebtors) ? state.selectedDebtors : [];
    return list.filter(Boolean);
  }, [state.selectedDebtors]);

  const previewDebtor = useMemo(() => {
    if (!selectedDebtors.length) return null;
    const idx = Math.min(Math.max(0, previewIndex), selectedDebtors.length - 1);
    return selectedDebtors[idx] || selectedDebtors[0] || null;
  }, [previewIndex, selectedDebtors]);

  const debtorOptions = useMemo(() => selectedDebtors.map((d, idx) => ({
    value: idx,
    label: d.type === 'P' ? `${d.name || '个人'} (${d.idNo || '-'})` : `${d.companyName || '企业'} (${d.socialCreditCode || '-'})`,
  })), [selectedDebtors]);

  const placeholders = useMemo(() => {
    try {
      return extractPlaceholders(String(state.contentHtml || ''));
    } catch {
      return [];
    }
  }, [state.contentHtml]);

  const placeholderFieldMap = useMemo(() => (state.placeholderFieldMap || {}), [state.placeholderFieldMap]);
  const placeholderManualMap = useMemo(() => (state.placeholderManualMap || {}), [state.placeholderManualMap]);

  const buildValueMapForDebtor = useMemo(() => (debtorRow) => {
    const debt = selectedDebt?.debt || {};
    const creditor = selectedDebt?.creditor || {};
    const debtor = debtorRow || {};

    const today = dayjs().format('YYYY年MM月DD日');
    const debtOccurrenceDate = debt?.debtOccurrenceDate ? dayjs(debt.debtOccurrenceDate).format('YYYY年MM月DD日') : '';

    const creditorType = String(creditor?.creditorType || '');
    const creditorName = creditorType === 'P' ? (creditor?.name || '') : (creditor?.companyName || '');
    const creditorIdNo = creditorType === 'P' ? (creditor?.idCardNo || '') : (creditor?.socialCreditCode || '');

    const debtorType = String(debtor?.type || '');
    const debtorName = debtorType === 'P' ? (debtor?.name || '') : (debtor?.companyName || '');
    const debtorIdNo = debtorType === 'P' ? (debtor?.idNo || '') : (debtor?.socialCreditCode || '');

    return {
      'today': today,
      'debt.debtNo': debt?.debtNo || '',
      'debt.title': debt?.title || '',
      'debt.debtType': labelOfDebtType(debt?.debtType),
      'debt.debtOccurrenceDate': debtOccurrenceDate,
      'debt.principalAmount': formatAmountText(debt?.principalAmount),
      'debt.totalAmount': formatAmountText(debt?.totalAmount),

      'creditor.name': creditorName,
      'creditor.idNo': creditorIdNo,
      'creditor.phone': creditor?.phone || '',
      'creditor.email': creditor?.email || '',
      'creditor.companyName': creditor?.companyName || '',
      'creditor.socialCreditCode': creditor?.socialCreditCode || '',
      'creditor.legalPersonName': creditor?.legalPersonName || '',
      'creditor.legalPersonIdNo': creditor?.legalPersonIdNo || '',
      'creditor.province': creditor?.province || '',
      'creditor.city': creditor?.city || '',
      'creditor.address': creditor?.address || '',

      'debtor.name': debtorName,
      'debtor.idNo': debtorIdNo,
      'debtor.phone': debtor?.phone || '',
      'debtor.email': debtor?.email || '',
      'debtor.companyName': debtor?.companyName || '',
      'debtor.socialCreditCode': debtor?.socialCreditCode || '',
      'debtor.legalPersonName': debtor?.legalPersonName || '',
      'debtor.legalPersonIdNo': debtor?.legalPersonIdNo || '',
    };
  }, [selectedDebt]);

  const previewValues = useMemo(() => {
    if (!previewDebtor) return {};
    const valueMap = buildValueMapForDebtor(previewDebtor);
    const out = {};
    (placeholders || []).forEach((ph) => {
      const manual = String((placeholderManualMap || {})[ph] || '').trim();
      if (manual) {
        out[ph] = manual;
        return;
      }
      const fk = String((placeholderFieldMap || {})[ph] || '').trim();
      if (!fk) {
        out[ph] = '';
        return;
      }
      const v = valueMap[fk];
      out[ph] = v === undefined || v === null ? '' : String(v);
    });
    return out;
  }, [buildValueMapForDebtor, placeholders, placeholderFieldMap, placeholderManualMap, previewDebtor]);

  const { previewPdfData, previewPdfLoading, previewPdfError } = usePreviewNoticeBodyPdf({
    debtId: id || state.selectedDebtId,
    previewDebtor,
    contentHtml: state.contentHtml,
    pageSettings: state.pageSettings,
    previewValues,
  });

  // 智能落章只在“尚无历史偏移”的前提下执行一次，避免覆盖用户已调整的位置
  // 注意：智能落章的推荐值也会被保存，便于后续点击“重置位置”回到推荐位置
  const enableSmartSeal = !!previewPdfData && !!sealUrl && !!applySignature && !hasInitialSealOffsets && !autoSealAppliedRef.current;
  const { suggestedOffsets } = useSmartSealDefaultPosition({
    enabled: enableSmartSeal,
    pdfData: previewPdfData,
    pageSettings: state.pageSettings,
    sealWidthMm,
    sealAspect,
    keywords: ['电子签章', '签章', '印章', '盖章', '签名'],
  });

  useEffect(() => {
    if (!suggestedOffsets) return;
    const r = clampNumber(Number(suggestedOffsets.sealOffsetRightMm || 0), 0, Number(maxOffsets.maxRight || 0));
    const b = clampNumber(Number(suggestedOffsets.sealOffsetBottomMm || 0), 0, Number(maxOffsets.maxBottom || 0));
    if (!Number.isFinite(r) || !Number.isFinite(b)) return;
    lastSmartOffsetsRef.current = { sealOffsetRightMm: r, sealOffsetBottomMm: b };
    if (!enableSmartSeal || autoSealAppliedRef.current) return;
    // 将智能推荐结果写入状态并持久化，后续“重置位置”可回到该推荐值
    setSealOffsetRightMm(r);
    setSealOffsetBottomMm(b);
    saveState({ sealOffsetRightMm: r, sealOffsetBottomMm: b, sealOffsetsSig: noticeContentSig });
    autoSealAppliedRef.current = true;
  }, [enableSmartSeal, maxOffsets.maxBottom, maxOffsets.maxRight, noticeContentSig, suggestedOffsets]);

  const sealTypeOptions = useMemo(() => ([
    { value: 'E', label: '企业公章' },
    { value: 'P', label: '个人印签' },
  ]), []);

  const handleSelectSeal = (seal) => {
    const nextId = seal?.id ?? null;
    const url = seal?.url || '';
    setSealId(nextId);
    setSealUrl(url);
    setSealAspect(1);
    saveState({ sealId: nextId, sealUrl: url, sealAspect: 1 });
    setLastSealByType((prev) => {
      const next = { ...(prev || {}), [sealType]: { id: nextId, url } };
      saveState({ lastSealByType: next });
      return next;
    });
  };

  const filteredSeals = useMemo(() => {
    const q = String(sealKeyword || '').trim().toLowerCase();
    const list = Array.isArray(sealList) ? sealList : [];
    return list
      .filter((s) => {
        if (!q) return true;
        const name = String(s?.sealName || '').toLowerCase();
        return name.includes(q);
      })
      .slice()
      .sort((a, b) => {
        const an = String(a?.sealName || '');
        const bn = String(b?.sealName || '');
        const byName = an.localeCompare(bn, 'zh');
        if (byName !== 0) return byName;
        const aId = String(a?.id ?? '');
        const bId = String(b?.id ?? '');
        return aId.localeCompare(bId, 'zh');
      });
  }, [sealKeyword, sealList]);

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => {
      const d = dragRef.current;
      if (Number.isFinite(Number(d.pointerId)) && e.pointerId !== d.pointerId) return;
      const rect = d.rect;
      const ps = d.pageSettings;
      if (!rect || !ps) return;
      // 将鼠标像素位移换算为 mm 位移，确保拖拽与页面 mm 尺寸/边距一致
      const mmPerPxX = Number(ps.widthMm || 210) / Math.max(1, rect.width);
      const mmPerPxY = Number(ps.heightMm || 297) / Math.max(1, rect.height);
      const dxMm = (e.clientX - d.startX) * mmPerPxX;
      const dyMm = (e.clientY - d.startY) * mmPerPxY;

      // 限制印章仅在内容区内移动：最大偏移 = 内容区宽高 - 印章宽高
      const contentW = Number(ps.widthMm || 210) - Number(ps.marginLeftMm || 0) - Number(ps.marginRightMm || 0);
      const contentH = Number(ps.heightMm || 297) - Number(ps.marginTopMm || 0) - Number(ps.marginBottomMm || 0);
      const stampW = Number(offsetsRef.current.widthMm || 0);
      const stampH = Number(offsetsRef.current.heightMm || 0);
      const maxRight = Math.max(0, contentW - stampW);
      const maxBottom = Math.max(0, contentH - stampH);

      const nextRight = clamp(d.startRightMm - dxMm, 0, maxRight);
      const nextBottom = clamp(d.startBottomMm - dyMm, 0, maxBottom);
      d.lastRightMm = nextRight;
      d.lastBottomMm = nextBottom;
      setSealOffsetRightMm(nextRight);
      setSealOffsetBottomMm(nextBottom);
    };
    const handleUp = () => {
      setDragging(false);
      const d = dragRef.current;
      const rightMm = Number.isFinite(Number(d.lastRightMm)) ? Number(d.lastRightMm) : offsetsRef.current.rightMm;
      const bottomMm = Number.isFinite(Number(d.lastBottomMm)) ? Number(d.lastBottomMm) : offsetsRef.current.bottomMm;
      saveState({ sealOffsetRightMm: rightMm, sealOffsetBottomMm: bottomMm, sealOffsetsSig: noticeContentSig });
    };
    const handleCancel = () => handleUp();
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp, { once: true });
    window.addEventListener('pointercancel', handleCancel, { once: true });
    window.addEventListener('blur', handleCancel, { once: true });
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleCancel);
      window.removeEventListener('blur', handleCancel);
    };
  }, [dragging, noticeContentSig]);

  const overlay = useMemo(() => {
    if (!applySignature || !sealUrl) return null;
    return ({ pageIndex, pageCount, pageSettings, cssWidthPx, cssHeightPx }) => {
      if (pageIndex !== pageCount - 1) return null;
      const ps = pageSettings || DEFAULT_PAGE_SETTINGS;
      const pxPerMmX = Number(cssWidthPx || 0) / Math.max(1, Number(ps.widthMm || 210));
      const pxPerMmY = Number(cssHeightPx || 0) / Math.max(1, Number(ps.heightMm || 297));
      // 偏移量定义：相对“内容区右下角”的距右/距底（mm），渲染到预览时需要加上页面边距（marginRight/marginBottom）
      const rightPx = (Number(ps.marginRightMm || 0) + sealOffsetRightMm) * pxPerMmX;
      const bottomPx = (Number(ps.marginBottomMm || 0) + sealOffsetBottomMm) * pxPerMmY;
      const widthPx = Number(sealWidthMm || 0) * pxPerMmX;
      const heightPx = Number(sealWidthMm || 0) * Number(sealAspect || 1) * pxPerMmY;
      return (
        <div
          style={{
            position: 'absolute',
            right: `${rightPx}px`,
            bottom: `${bottomPx}px`,
            width: `${widthPx}px`,
            height: `${heightPx}px`,
            cursor: 'move',
            pointerEvents: 'auto',
            touchAction: 'none',
            userSelect: 'none',
            zIndex: 2,
          }}
          onPointerDown={(e) => {
            if (!applySignature) return;
            e.preventDefault();
            try { e.currentTarget?.setPointerCapture?.(e.pointerId); } catch (_) {}
            const rect = e.currentTarget?.parentElement?.getBoundingClientRect?.();
            if (!rect) return;
            dragRef.current = {
              startX: e.clientX,
              startY: e.clientY,
              pointerId: e.pointerId,
              startRightMm: sealOffsetRightMm,
              startBottomMm: sealOffsetBottomMm,
              rect,
              pageSettings: ps,
              lastRightMm: sealOffsetRightMm,
              lastBottomMm: sealOffsetBottomMm,
            };
            setDragging(true);
          }}
        >
          <img
            src={sealUrl}
            alt="seal"
            draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: 0.9, userSelect: 'none', pointerEvents: 'none' }}
          />
        </div>
      );
    };
  }, [applySignature, sealAspect, sealOffsetBottomMm, sealOffsetRightMm, sealUrl, sealWidthMm]);

  const steps = [
    { title: '选择债权' },
    { title: '选择模板' },
    { title: '编辑内容' },
    { title: '签章与预览' },
    { title: '生成催告文件' },
    { title: '发送催告' },
    { title: '归档' },
  ];
  const currentStep = 3;

  return (
    <div className="page-container" style={{ padding: 24 }}>
      <h1 className="page-title">发布催告</h1>
      <Card className="card-compact" size="small" style={{ marginBottom: 16 }}>
        {isMobile ? (
          <div className="c-steps-mobile-header" aria-label="步骤信息">
            <div className="c-steps-mobile-left">
              <span className="c-steps-mobile-stepno">{`第 ${currentStep + 1} 步`}</span>
              <span className="c-steps-mobile-title">{steps[currentStep]?.title || ''}</span>
            </div>
            <div className="c-steps-mobile-progress">{`${currentStep + 1}/${steps.length}`}</div>
          </div>
        ) : (
          <Steps current={currentStep} size="small" items={steps} />
        )}
      </Card>

      <Space
        style={{ marginBottom: 16, width: isMobile ? '100%' : undefined }}
        direction={isMobile ? 'vertical' : 'horizontal'}
        wrap={!isMobile}
      >
        <Button onClick={prev} block={isMobile}>上一步</Button>
        <Button
          type="primary"
          block={isMobile}
          onClick={() => {
            if (applySignature && !sealUrl) {
              message.warning('已启用签章，请先选择印签');
              return;
            }
            next();
          }}
        >
          下一步
        </Button>
      </Space>

      <div style={{ display: 'flex', gap: 12 }}>
        <Card
          style={{ flex: '0 0 420px' }}
          title="签章设置"
          size="small"
        >
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
              <Text>启用签章叠加</Text>
              <Switch
                checked={applySignature}
                onChange={(v) => {
                  setApplySignature(v);
                  saveState({ applySignature: v });
                }}
              />
            </Space>

            <Divider style={{ margin: '8px 0' }} />

            <Radio.Group
              value={sealType}
              onChange={(e) => {
                const nextType = e?.target?.value === 'E' ? 'E' : 'P';
                setSealType(nextType);
                setSealTypeTouched(true);
                setSealKeyword('');
                if (!sealWidthTouched && !hasInitialSealWidth) {
                  const nextWidth = nextType === 'E' ? DEFAULT_SEAL_WIDTH_MM_ENTERPRISE : DEFAULT_SEAL_WIDTH_MM_PERSONAL;
                  const clamped = clampSealWidthMm(nextWidth);
                  setSealWidthMm(clamped);
                  saveState({ sealWidthMm: clamped });
                }
                const last = lastSealByType?.[nextType] || null;
                const nextId = last?.id ?? null;
                const nextUrl = last?.url || '';
                setSealId(nextId);
                setSealUrl(nextUrl);
                setSealAspect(1);
                saveState({ sealType: nextType, sealId: nextId, sealUrl: nextUrl, sealAspect: 1 });
              }}
              optionType="button"
              buttonStyle="solid"
              disabled={!applySignature || sealLoading}
              style={{ width: '100%', display: 'flex' }}
            >
              {sealTypeOptions.map((opt) => (
                <Radio.Button
                  key={opt.value}
                  value={opt.value}
                  style={{ flex: 1, textAlign: 'center' }}
                >
                  {opt.label}
                </Radio.Button>
              ))}
            </Radio.Group>

            <Input.Search
              value={sealKeyword}
              onChange={(e) => setSealKeyword(e?.target?.value || '')}
              placeholder={sealLoading ? '加载中...' : '搜索印章名称'}
              allowClear
              disabled={!applySignature || sealLoading}
            />

            <div
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                padding: 10,
                background: '#fff',
                opacity: applySignature ? 1 : 0.6,
                pointerEvents: applySignature ? 'auto' : 'none',
              }}
            >
              {filteredSeals.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无可选印章" />
              ) : (
                <div style={{ maxHeight: 320, overflow: 'auto', paddingRight: 4 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                    {filteredSeals.map((s) => {
                      const sid = String(s?.id ?? '');
                      const selected = String(sealId ?? '') === sid;
                      return (
                        <div
                          key={sid}
                          onClick={() => handleSelectSeal(s)}
                          style={{
                            border: selected ? '1px solid #1677ff' : '1px solid #f0f0f0',
                            borderRadius: 8,
                            padding: 8,
                            cursor: 'pointer',
                            background: selected ? '#e6f4ff' : '#fff',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <div
                            style={{
                              position: 'relative',
                              height: 120,
                              borderRadius: 6,
                              background: '#fafafa',
                              overflow: 'hidden',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {s?.url ? (
                              <img
                                src={s.url}
                                alt={s?.sealName || 'seal'}
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                draggable={false}
                              />
                            ) : (
                              <span style={{ color: '#999', fontSize: 12 }}>无图片</span>
                            )}
                          </div>
                          <div style={{ marginTop: 6, fontSize: 12, color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {s?.sealName || `印章#${sid}`}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {applySignature && !sealUrl ? (
              <Text type="secondary">未选择印签，预览不会叠加</Text>
            ) : null}

            {sealUrl ? (
              <div style={{ background: '#fafafa', borderRadius: 6, padding: 12 }}>
                <Image src={sealUrl} alt="seal" style={{ maxWidth: '100%' }} />
              </div>
            ) : null}

            <Divider style={{ margin: '8px 0' }} />

            <Space wrap>
              <div>
                <Text type="secondary">宽度(mm)</Text>
                <div>
                  <InputNumber
                    min={sealWidthLimits.min}
                    max={sealWidthLimits.max}
                    value={sealWidthMm}
                    disabled
                    // 禁用修改以符合国家标准，避免用户随意调整签章大小
                  />
                </div>
              </div>
              <div>
                <Text type="secondary">距右(mm)</Text>
                <div>
                  <InputNumber
                    min={0}
                    max={Math.max(0, Number(maxOffsets.maxRight || 0))}
                    value={sealOffsetRightMm}
                    onChange={(v) => {
                      const nv = clampNumber(Number(v || 0), 0, Number(maxOffsets.maxRight || 0));
                      setSealOffsetRightMm(nv);
                      saveState({ sealOffsetRightMm: nv, sealOffsetsSig: noticeContentSig });
                    }}
                    disabled={!applySignature}
                  />
                </div>
              </div>
              <div>
                <Text type="secondary">距底(mm)</Text>
                <div>
                  <InputNumber
                    min={0}
                    max={Math.max(0, Number(maxOffsets.maxBottom || 0))}
                    value={sealOffsetBottomMm}
                    onChange={(v) => {
                      const nv = clampNumber(Number(v || 0), 0, Number(maxOffsets.maxBottom || 0));
                      setSealOffsetBottomMm(nv);
                      saveState({ sealOffsetBottomMm: nv, sealOffsetsSig: noticeContentSig });
                    }}
                    disabled={!applySignature}
                  />
                </div>
              </div>
              <Button
                onClick={() => {
                  setDragging(false);
                  const off = lastSmartOffsetsRef.current;
                  const r = Number(off?.sealOffsetRightMm);
                  const b = Number(off?.sealOffsetBottomMm);
                  if (Number.isFinite(r) && Number.isFinite(b)) {
                    setSealOffsetRightMm(r);
                    setSealOffsetBottomMm(b);
                    saveState({ sealOffsetRightMm: r, sealOffsetBottomMm: b, sealOffsetsSig: noticeContentSig });
                    autoSealAppliedRef.current = true;
                    return;
                  }
                  const dr = clampNumber(8, 0, Number(maxOffsets.maxRight || 0));
                  const db = clampNumber(8, 0, Number(maxOffsets.maxBottom || 0));
                  setSealOffsetRightMm(dr);
                  setSealOffsetBottomMm(db);
                  saveState({ sealOffsetRightMm: dr, sealOffsetBottomMm: db, sealOffsetsSig: noticeContentSig });
                }}
                disabled={!applySignature}
              >
                重置位置
              </Button>
            </Space>

            <Text type="secondary">可拖动预览中的印章调整位置；当前预览仅叠加到最后一页</Text>
          </Space>
        </Card>

        <Card style={{ flex: '1 1 0' }} title="预览" size="small">
          {selectedDebtors.length ? (
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <Space wrap>
                <span style={{ color: '#666' }}>债务人：</span>
                <Pagination
                  size="small"
                  current={selectedDebtors.length ? (previewIndex + 1) : 1}
                  total={selectedDebtors.length}
                  pageSize={1}
                  showSizeChanger={false}
                  showQuickJumper={selectedDebtors.length > 20}
                  onChange={(page) => {
                    const idx = Math.max(0, Math.min(selectedDebtors.length - 1, Number(page || 1) - 1));
                    setPreviewIndex(idx);
                  }}
                />
                <Select
                  value={previewIndex}
                  style={{ width: 360 }}
                  options={debtorOptions}
                  placeholder="选择债务人"
                  onChange={(idx) => setPreviewIndex(Number(idx || 0))}
                />
              </Space>
              <span style={{ color: '#999' }}>
                {previewDebtor
                  ? (previewDebtor.type === 'P'
                    ? `${previewDebtor.name || '个人'} (${previewDebtor.idNo || '-'})`
                    : `${previewDebtor.companyName || '企业'} (${previewDebtor.socialCreditCode || '-'})`)
                  : ''}
              </span>
            </div>
          ) : (
            <Text type="secondary">未选择债务人，无法预览</Text>
          )}

          <div>
            {previewPdfError ? <Alert type="warning" showIcon message={previewPdfError} style={{ marginBottom: 12 }} /> : null}
            {previewPdfLoading ? (
              <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>
            ) : (
              previewPdfData ? (
                <PdfPreview
                  pdfData={previewPdfData}
                  pageSettings={state.pageSettings}
                  renderPageOverlay={overlay}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无预览" />
              )
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PublishNoticeSignaturePage;
